import { FormEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { Control } from "react-hook-form";
import { Button } from "@carbon/react";
import TextField from "../../../components/carbon/Form/TextField";
import LinePriceTable from "../../../components/LinePriceTable/LinePriceTable";
import { ServiceLine } from "../../../types";
import { LinePriceInputs } from "../../../utils";
import TextareaField from "../../../components/carbon/Form/TextareaField";
import { AnonymousOfferFormValues } from "../offerSchema";

interface OfferFormProps {
  control: Control<AnonymousOfferFormValues>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  isUpdate: boolean;
  /** The buyer collects (SCRUM-70): ask for the pickup address instead of a delivery cost. */
  buyerPicksUp?: boolean;
  /** SCRUM-116: the lines of a service quoted by line — priced one by one, the price is their total. */
  pricedLines?: ServiceLine[];
  linePrices?: LinePriceInputs;
  onLinePriceChange?: (code: string, unitPrice: string) => void;
  onLineCurrencyChange?: (code: string, currency: string) => void;
  showMissingLinePrices?: boolean;
}

/**
 * The anonymous supplier's offer form — supplier identity (email/taxId/companyName) + the offer
 * fields (price/quantity/terms/dates/comments), on the shared Controller-bound Carbon fields.
 * `POST /order-invitations/offer` lifecycle stays with the Offers flow; this only renders the entry.
 */
const OfferForm = ({
  control,
  onSubmit,
  isUpdate,
  buyerPicksUp = false,
  pricedLines = [],
  linePrices = {},
  onLinePriceChange,
  onLineCurrencyChange,
  showMissingLinePrices = false,
}: OfferFormProps) => {
  const { t } = useTranslation();
  const pricedByLine = pricedLines.length > 0;

  return (
    <form onSubmit={onSubmit} className="anonymous-offer__form">
      <fieldset className="anonymous-offer__section">
        <legend className="anonymous-offer__legend">
          {t("anonymousOffer.supplierSection")}
        </legend>
        <TextField
          control={control}
          name="email"
          type="email"
          label={t("anonymousOffer.email")}
        />
        <TextField
          control={control}
          name="taxId"
          label={t("anonymousOffer.taxId")}
        />
        <TextField
          control={control}
          name="companyName"
          label={t("anonymousOffer.companyName")}
          optional
        />
      </fieldset>

      <fieldset className="anonymous-offer__section">
        <legend className="anonymous-offer__legend">
          {t("anonymousOffer.offerSection")}
        </legend>
        {pricedByLine && (
          <div className="anonymous-offer__line-prices">
            <p className="anonymous-offer__hint">{t("linePricing.hint")}</p>
            <LinePriceTable
              lines={pricedLines}
              prices={linePrices}
              onPriceChange={onLinePriceChange}
              onCurrencyChange={onLineCurrencyChange}
              showMissing={showMissingLinePrices}
              showDetail={false}
            />
          </div>
        )}
        <div className="anonymous-offer__grid">
          {/* A line-priced service takes its price and currency from its lines. */}
          {!pricedByLine && (
            <TextField
              control={control}
              name="price"
              type="number"
              step="any"
              label={t("anonymousOffer.price")}
            />
          )}
          <TextField
            control={control}
            name="quantity"
            type="number"
            step="any"
            label={t("anonymousOffer.quantityField")}
          />
          <TextField
            control={control}
            name="discount"
            type="number"
            step="any"
            label={t("anonymousOffer.discount")}
            optional
          />
          {buyerPicksUp ? (
            <TextField
              control={control}
              name="pickupAddress"
              label={t("offer.pickupAddress")}
              helperText={t("offer.pickupAddressHint")}
            />
          ) : (
            <TextField
              control={control}
              name="deliveryCost"
              type="number"
              step="any"
              label={t("anonymousOffer.deliveryCost")}
              optional
            />
          )}
          <TextField
            control={control}
            name="deliveryDate"
            type="date"
            label={t("anonymousOffer.deliveryDate")}
            optional
          />
          <TextField
            control={control}
            name="expireDate"
            type="date"
            label={t("anonymousOffer.expireDate")}
            optional
          />
          <TextField
            control={control}
            name="offerValidity"
            label={t("anonymousOffer.offerValidity")}
            optional
          />
          <TextField
            control={control}
            name="leadTime"
            label={t("anonymousOffer.leadTime")}
            optional
          />
          {!pricedByLine && (
            <TextField
              control={control}
              name="currency"
              label={t("anonymousOffer.currency")}
              optional
            />
          )}
          <TextField
            control={control}
            name="paymentTerm"
            label={t("anonymousOffer.paymentTerm")}
            optional
          />
          <TextField
            control={control}
            name="paymentMethod"
            label={t("anonymousOffer.paymentMethod")}
            optional
          />
          <TextField
            control={control}
            name="deliveryFormat"
            label={t("anonymousOffer.deliveryFormat")}
            optional
          />
        </div>
        <div className="anonymous-offer__comments">
          <TextareaField
            control={control}
            name="comment"
            label={t("anonymousOffer.comment")}
            rows={3}
            optional
          />
          <TextareaField
            control={control}
            name="generalComments"
            label={t("anonymousOffer.generalComments")}
            rows={3}
            optional
          />
        </div>
      </fieldset>

      <Button type="submit" className="anonymous-offer__submit">
        {isUpdate ? t("anonymousOffer.update") : t("anonymousOffer.submit")}
      </Button>
    </form>
  );
};

export default OfferForm;
